import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, map } from 'rxjs';

import {
  CommunicationWidgetName,
  CommunicationWidgetOfferReservation,
  CommunicationWidgetOfferReservationSchema,
} from '@pwp-common';

import { CommunicationWidgetOfferReservationService } from '../../../../services/communication/communication-widget/communication-widget-offer-reservation/communication-widget-offer-reservation.service';
import { makeAuditEntry } from '../../../generic/settings/common/audit-entry/helpers/make-audit-entry';
import { AuditEntry } from '../../../generic/settings/common/audit-entry/interfaces';

@UntilDestroy()
@Component({
  selector: 'app-communication-workflow-global-settings',
  templateUrl: './communication-workflow-global-settings.component.html',
  styleUrls: ['./communication-workflow-global-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommunicationWorkflowGlobalSettingsComponent implements OnInit {
  private communicationWidgetOfferReservation: CommunicationWidgetOfferReservation;

  public auditEntry: AuditEntry;

  public readonly form = new FormGroup({
    noisyEnvironment: new FormControl<boolean>(false),
  });

  ///////////////////////////////////////////////////////////////////////
  // Constructor
  ///////////////////////////////////////////////////////////////////////

  constructor(
    private communicationWidgetOfferReservationService: CommunicationWidgetOfferReservationService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    this.communicationWidgetOfferReservationService
      .getDefaultDoc()
      .pipe(
        untilDestroyed(this),
        map((z) => {
          this.communicationWidgetOfferReservation = z;
          this.resetUI();
        }),
      )
      .subscribe();
  }

  public refreshData = async () => {
    this.communicationWidgetOfferReservation = await firstValueFrom(
      this.communicationWidgetOfferReservationService.getDefaultDoc(),
    );
    this.resetUI();
  };

  public resetUI = () => {
    const allowSpeech = this.communicationWidgetOfferReservation?.getAllowSpeech() ?? true;
    this.form.patchValue({ noisyEnvironment: !allowSpeech });
    this.auditEntry = makeAuditEntry(this.communicationWidgetOfferReservation);
    this.changeDetectorRef.markForCheck();
  };

  ///////////////////////////////////////////////////////////////////////////////////////////
  // Upload
  ///////////////////////////////////////////////////////////////////////////////////////////

  public upload = async () => {
    const obj = new CommunicationWidgetOfferReservation({
      id: this.communicationWidgetOfferReservation?.getId(),
      type: CommunicationWidgetName.offerReservation,
      allowSpeech: !this.noisyEnvironment?.value,
      description: CommunicationWidgetOfferReservationSchema.Defaults.description,
      displayName: CommunicationWidgetOfferReservationSchema.Defaults.displayName,
    });
    await this.communicationWidgetOfferReservationService.uploadDefaultDoc(obj);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Controls
  /////////////////////////////////////////////////////////////////////////////////////////////

  public get noisyEnvironment() {
    return this.form.get('noisyEnvironment') as FormControl<boolean>;
  }
}
